/**
 * useGuardPatrolLocations Hook
 *
 * Derives patrol locations for a specific guard's shift with sequence numbers.
 * Used when a guard is selected to filter the map to only show their patrol sites.
 *
 * Features:
 * - Fetches guard's shift patrols via useGuardShift
 * - Matches patrols to locations by siteName to get coordinates
 * - Assigns sequence numbers based on scheduled start time
 * - Returns filtered locations ready for numbered marker rendering
 *
 * Note: ShiftPatrol from the API does not include coordinates, so we match
 * by siteName against the patrol locations data source (either from guards
 * or from the patrol locations API).
 */

import { useMemo } from 'react';
import { useGuardShift } from './useGuardShift';
import { PatrolLocation, PatrolStatus } from '../../components/guardLocation/types';
import { ShiftPatrol } from '../../../../../api/dispatch/types';

/** Extended patrol location with sequence number for filtered guard view */
export interface GuardPatrolLocation extends PatrolLocation {
  /** Sequence number (1, 2, 3...) based on scheduled start order */
  sequenceNumber: number;
  /** Schedule assignment ID for opening patrol detail modal */
  scheduleAssignmentId: number;
  /** Scheduled start time for ordering */
  scheduledStart: string;
  /** Scheduled end time */
  scheduledEnd: string;
}

export interface UseGuardPatrolLocationsOptions {
  /** The guard ID to fetch shift patrols for */
  guardId: number | null;
  /** All patrol locations (from usePatrolLocations or usePatrolLocationsApi) */
  allPatrolLocations: PatrolLocation[];
}

export interface UseGuardPatrolLocationsResult {
  /** Guard's patrol locations with sequence numbers */
  guardPatrolLocations: GuardPatrolLocation[];
  /** Guard name for display */
  guardName: string | null;
  /** Total patrols in shift */
  totalPatrols: number;
  /** Completed patrols count */
  completedPatrols: number;
  /** Whether data is loading */
  isLoading: boolean;
  /** Whether any fetch is in progress */
  isFetching: boolean;
  /** Whether an error occurred */
  isError: boolean;
  /** Error object if present */
  error: Error | null;
}

/**
 * Normalize site name for matching (lowercase, trim, remove extra spaces)
 */
function normalizeSiteName(name: string | null | undefined): string {
  if (!name) return '';
  return name.toLowerCase().trim().replace(/\s+/g, ' ');
}

/**
 * Match shift patrol to patrol location.
 * Prefers addressId match (reliable), falls back to site name matching.
 */
function findMatchingLocation(
  shiftPatrol: ShiftPatrol,
  allLocations: PatrolLocation[]
): PatrolLocation | null {
  // Primary: match by addressId (exact, reliable)
  if (shiftPatrol.addressId != null) {
    const byId = allLocations.find((loc) => loc.addressId === shiftPatrol.addressId);
    if (byId) return byId;
  }

  // Fallback: match by site name (for backwards compatibility)
  const normalizedShiftSite = normalizeSiteName(shiftPatrol.siteName);

  for (const location of allLocations) {
    const normalizedLocationSite = normalizeSiteName(location.siteName);
    if (normalizedLocationSite === normalizedShiftSite) {
      return location;
    }
  }

  for (const location of allLocations) {
    const normalizedLocationSite = normalizeSiteName(location.siteName);
    if (
      normalizedLocationSite.includes(normalizedShiftSite) ||
      normalizedShiftSite.includes(normalizedLocationSite)
    ) {
      return location;
    }
  }

  return null;
}

/**
 * Hook to get patrol locations for a specific guard's shift with sequence numbers.
 *
 * @param options - Configuration options including guardId and all patrol locations
 * @returns Guard patrol locations with loading/error states
 */
export function useGuardPatrolLocations(
  options: UseGuardPatrolLocationsOptions
): UseGuardPatrolLocationsResult {
  const { guardId, allPatrolLocations } = options;

  // Fetch guard's shift data
  // FIX: Only enable query when allPatrolLocations is ready to prevent race condition
  // where shift data arrives before location data, causing matching to fail
  const {
    guardName,
    patrols: shiftPatrols,
    totalPatrols,
    completedPatrols,
    isLoading: shiftLoading,
    isFetching,
    isError,
    error,
  } = useGuardShift({
    guardId,
    enabled: guardId !== null && allPatrolLocations.length > 0,
  });

  // Track if we're waiting for patrol locations to be ready
  const isWaitingForLocations = guardId !== null && allPatrolLocations.length === 0;
  const isLoading = shiftLoading || isWaitingForLocations;

  // Derive guard patrol locations with sequence numbers
  const guardPatrolLocations = useMemo<GuardPatrolLocation[]>(() => {
    if (!shiftPatrols || shiftPatrols.length === 0) {
      return [];
    }

    // Sort by scheduled start time to determine sequence
    const sortedPatrols = [...shiftPatrols].sort((a, b) => {
      const dateA = new Date(a.scheduledStart).getTime();
      const dateB = new Date(b.scheduledStart).getTime();
      return dateA - dateB;
    });

    // Match each patrol to a location and add sequence number
    const matchedLocations: GuardPatrolLocation[] = [];
    const seenAddressIds = new Set<number>();

    sortedPatrols.forEach((shiftPatrol, index) => {
      const matchedLocation = findMatchingLocation(shiftPatrol, allPatrolLocations);

      if (matchedLocation) {
        // Avoid duplicates (same site can appear multiple times in shift)
        if (seenAddressIds.has(matchedLocation.addressId)) {
          return;
        }
        seenAddressIds.add(matchedLocation.addressId);

        // Recalculate circleStatus when patrolStatus is overridden from shift data,
        // otherwise the stale circleStatus from the base location is kept (e.g., 'guard_outside'
        // even though the patrol is completed)
        const updatedPatrolStatus = shiftPatrol.status;
        const updatedCircleStatus = updatedPatrolStatus === 'completed'
          ? 'completed' as const
          : matchedLocation.circleStatus;

        matchedLocations.push({
          ...matchedLocation,
          // Override patrol status and circle status from shift data (more current)
          patrolStatus: updatedPatrolStatus,
          circleStatus: updatedCircleStatus,
          // Add sequence and schedule data
          sequenceNumber: index + 1,
          scheduleAssignmentId: shiftPatrol.scheduleAssignmentId,
          scheduledStart: shiftPatrol.scheduledStart,
          scheduledEnd: shiftPatrol.scheduledEnd,
        });
      }
    });

    return matchedLocations;
  }, [shiftPatrols, allPatrolLocations]);

  return {
    guardPatrolLocations,
    guardName,
    totalPatrols,
    completedPatrols,
    isLoading,
    isFetching,
    isError,
    error,
  };
}

export default useGuardPatrolLocations;
